import { useMemo } from 'react'
import { Chart } from 'react-chartjs-2'
import type {
  ChartData,
  ChartOptions,
  ChartType as ChartJSType,
} from 'chart.js'
import { ensureChartsRegistered } from './register'
import { CHART_COLORS, useDarkMode } from './palette'

ensureChartsRegistered()

/**
 * Catch-all chart renderer that takes a near-raw Chart.js config
 * (`type` + `data` + optional `options`). Use this for chart variants
 * our named wrappers don't cover: stacked bars, mixed bar+line,
 * horizontal bars, area fills, dual axes, custom colors, etc.
 *
 * We apply sensible theme defaults (grid colors, tick colors, legend
 * position, axis title colors) on top of whatever the caller passes,
 * so charts still look consistent in light/dark mode without the
 * caller having to specify them.
 */

export interface CustomChartProps {
  chartType: ChartJSType
  data: ChartData<ChartJSType>
  options?: ChartOptions<ChartJSType>
  height?: number | string
  className?: string
  ariaLabel?: string
}

const PALETTE = [
  '#2563eb', '#16a34a', '#dc2626', '#ca8a04', '#9333ea',
  '#0891b2', '#ea580c', '#4f46e5', '#db2777', '#0d9488',
]

function withFallbackColors(data: ChartData<ChartJSType>): ChartData<ChartJSType> {
  const datasets = Array.isArray(data.datasets) ? data.datasets : []
  const filled = datasets.map((ds, i) => {
    const base = PALETTE[i % PALETTE.length]
    const copy = { ...ds } as Record<string, unknown>
    if (copy.backgroundColor == null) copy.backgroundColor = base + 'CC'
    if (copy.borderColor == null) copy.borderColor = base
    return copy as unknown as typeof ds
  })
  return { ...data, datasets: filled }
}

export function CustomChart({
  chartType,
  data,
  options,
  height = 320,
  className,
  ariaLabel,
}: CustomChartProps) {
  const dark = useDarkMode()

  const mergedData = useMemo(() => withFallbackColors(data), [data])

  const mergedOptions = useMemo<ChartOptions<ChartJSType>>(() => {
    const grid = { color: dark ? CHART_COLORS.gridDark : CHART_COLORS.gridLight }
    const ticks = { color: dark ? CHART_COLORS.tickDark : CHART_COLORS.tickLight }
    const base: ChartOptions<ChartJSType> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: true, position: 'top' },
        datalabels: { display: false },
        ...(options?.plugins ?? {}),
      },
      scales: options?.scales,
    }
    // Only inject default x/y theming when the caller hasn't defined them
    // AND the chart type uses cartesian axes. Radial (radar/polar/pie/doughnut)
    // charts don't have x/y.
    const isCartesian = !['pie', 'doughnut', 'polarArea', 'radar'].includes(chartType)
    if (isCartesian && !base.scales) {
      base.scales = {
        x: { grid, ticks },
        y: { grid, ticks },
      } as ChartOptions<ChartJSType>['scales']
    }
    return { ...base, ...options, plugins: base.plugins, scales: base.scales ?? options?.scales }
  }, [dark, options, chartType])

  return (
    <div
      className={className}
      style={{ height, width: '100%' }}
      role="img"
      aria-label={ariaLabel}
    >
      <Chart type={chartType} data={mergedData} options={mergedOptions} />
    </div>
  )
}
